<!--
  SurveyWatcher.svelte
  This component watches for changes to the survey file and triggers updates
  without requiring a full page reload. Uses Vite's HMR WebSocket for instant updates.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { surveyStore } from '$lib/stores/surveyStore';
  import { interpretSurvey } from '$lib/utils/surveyInterpreter';
  import { page } from '$app/stores';
  import { invalidateAll } from '$app/navigation';

  // Track if we're in development mode
  const isDev = import.meta.env.DEV;

  async function updateSurveyData() {
    try {
      const currentPath = $page.url.pathname;

      // Check if we're on a dynamic page route ([page] or [page]/[slug])
      // These routes load data via +page.server.ts, not from survey.js
      const isPageRoute = !!$page.params.page;

      if (isPageRoute) {
        // For page routes, use invalidateAll to re-run server load functions
        // This tells SvelteKit to re-execute +page.server.ts which reads fresh YAML
        console.log('📢 Invalidating page data for fresh content:', currentPath);
        await invalidateAll();
        return;
      }

      // For main survey routes, update the survey store via dynamic import
      // Use timestamp to bust import cache
      const surveyModule = await import(`../../content/survey.js?t=${Date.now()}`);
      const newSurveyData = surveyModule.default;

      if (!newSurveyData) {
        throw new Error('Empty survey data received');
      }

      // Process the survey data
      const processedSurvey = interpretSurvey(newSurveyData);

      // Update the survey in the store
      surveyStore.updateSurvey(processedSurvey);

      // If we're on a specific question page, also invalidate to refresh
      if ($page.params.questionId) {
        await invalidateAll();
      }
    } catch (error) {
      console.error('Failed to update survey data:', error);
    }
  }

  onMount(() => {
    if (!isDev) return; // Only run in development mode

    // Listen for HMR events from vite-plugin-survey-hmr
    if (import.meta.hot) {
      import.meta.hot.on('survey-update', async (data) => {
        console.log('📢 Survey updated:', data.message);
        await updateSurveyData();
      });
    }
  });
</script>

<!-- No visible UI, just functionality --> 